import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axiosInstance from '../../utils/axiosConfig';
import LoaderInside from '../LoaderInside';
import backIcon from '../../assets/back-btn.svg';

const FAQContent = () => {
    const { category } = useParams();
    const navigate = useNavigate();
    const adminMode = useSelector((state) => state.adminMode.adminMode);

    const [loading, setLoading] = useState(true);
    const [faqs, setFaqs] = useState([]);
    const [editingFaq, setEditingFaq] = useState(null);
    const [tempText, setTempText] = useState('');
    const [tempHighlights, setTempHighlights] = useState([]);
    const [selection, setSelection] = useState(null);
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    const textAreaRef = useRef(null);

    // Функция для определения заголовка страницы по категории
    const getCategoryTitle = () => {
        switch (category) {
            case 'rules': return 'Правила игры';
            case 'learning': return 'Всё об обучении';
            case 'tasks': return 'Как выполнять задания';
            case 'prizes': return 'Как выйграть приз';
            default: return 'FAQ';
        }
    };

    // Загрузка данных FAQ по категории
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get(`/faq/${category}`);
                setFaqs(response.data);
                setLoading(false);
            } catch (error) {
                console.error('Ошибка при загрузке FAQ:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, [category]);

    // Функция для возврата на главную страницу FAQ
    const handleBack = () => {
        navigate('/faq');
    };

    // Функция для перехода на страницу создания новой FAQ
    const handleCreateNew = () => {
        navigate(`/faq/${category}/create`);
    };

    // Функция для начала редактирования FAQ
    const startEditing = (faq) => {
        setEditingFaq(faq);
        setTempText(faq.text || '');
        setTempHighlights(faq.highlights ? [...faq.highlights] : []);
    };

    // Функция для отмены редактирования
    const cancelEditing = () => {
        setEditingFaq(null);
        setTempText('');
        setTempHighlights([]);
        setSelection(null);
        setShowToolbar(false);
    };

    // Функция для сохранения изменений
    const saveChanges = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.put(`/faq/${editingFaq._id}`, {
                category,
                text: tempText,
                highlights: tempHighlights,
                order: editingFaq.order
            });

            if (response.data.success) {
                // Обновление списка FAQ
                const faqResponse = await axiosInstance.get(`/faq/${category}`);
                setFaqs(faqResponse.data);

                // Сброс состояния редактирования
                setEditingFaq(null);
                setTempText('');
                setTempHighlights([]);
                setSelection(null);
                setShowToolbar(false);
            } else {
                alert('Ошибка при обновлении FAQ: ' + response.data.error);
            }
            setLoading(false);
        } catch (error) {
            console.error('Ошибка при сохранении FAQ:', error);
            alert('Ошибка при обновлении FAQ. Возможно, у вас нет прав доступа.');
            setLoading(false);
        }
    };

    // Функция для удаления FAQ
    const deleteFAQ = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить эту запись FAQ?')) {
            return;
        }

        try {
            setLoading(true);
            const response = await axiosInstance.delete(`/faq/${id}`);

            if (response.data.success) {
                // Обновление списка FAQ
                const faqResponse = await axiosInstance.get(`/faq/${category}`);
                setFaqs(faqResponse.data);
            } else {
                alert('Ошибка при удалении FAQ: ' + response.data.error);
            }
            setLoading(false);
        } catch (error) {
            console.error('Ошибка при удалении FAQ:', error);
            alert('Ошибка при удалении FAQ. Возможно, у вас нет прав доступа.');
            setLoading(false);
        }
    };

    // Функция для изменения порядка (переместить выше)
    const moveUp = async (faq) => {
        const currentOrder = faq.order;

        try {
            setLoading(true);

            // Находим запись с предыдущим индексом (большим порядком отображения)
            const prevFaq = faqs
                .filter(item => item.order < currentOrder) // Меньшее значение = выше в списке
                .sort((a, b) => b.order - a.order)[0]; // Берем самый нижний из тех, что выше текущего

            if (!prevFaq) {
                setLoading(false);
                return; // Уже первый в списке
            }

            // Меняем местами порядок
            await axiosInstance.put(`/faq/${faq._id}`, {
                category,
                text: faq.text,
                highlights: faq.highlights,
                order: prevFaq.order
            });

            await axiosInstance.put(`/faq/${prevFaq._id}`, {
                category,
                text: prevFaq.text,
                highlights: prevFaq.highlights,
                order: currentOrder
            });

            // Обновляем список
            const faqResponse = await axiosInstance.get(`/faq/${category}`);
            setFaqs(faqResponse.data);
            setLoading(false);
        } catch (error) {
            console.error('Ошибка при изменении порядка:', error);
            alert('Ошибка при изменении порядка отображения. Пожалуйста, попробуйте еще раз.');
            setLoading(false);
        }
    };

    // Функция для изменения порядка (переместить ниже)
    const moveDown = async (faq) => {
        const currentOrder = faq.order;

        try {
            setLoading(true);

            // Находим запись со следующим индексом (меньшим порядком отображения)
            const nextFaq = faqs
                .filter(item => item.order > currentOrder) // Большее значение = ниже в списке
                .sort((a, b) => a.order - b.order)[0]; // Берем самый верхний из тех, что ниже текущего

            if (!nextFaq) {
                setLoading(false);
                return; // Уже последний в списке
            }

            // Меняем местами порядок
            await axiosInstance.put(`/faq/${faq._id}`, {
                category,
                text: faq.text,
                highlights: faq.highlights,
                order: nextFaq.order
            });

            await axiosInstance.put(`/faq/${nextFaq._id}`, {
                category,
                text: nextFaq.text,
                highlights: nextFaq.highlights,
                order: currentOrder
            });

            // Обновляем список
            const faqResponse = await axiosInstance.get(`/faq/${category}`);
            setFaqs(faqResponse.data);
            setLoading(false);
        } catch (error) {
            console.error('Ошибка при изменении порядка:', error);
            alert('Ошибка при изменении порядка отображения. Пожалуйста, попробуйте еще раз.');
            setLoading(false);
        }
    };

    // Функция для обновления текста
    const updateText = (newText) => {
        setTempText(newText);
    };

    // Функция для отслеживания выделения текста
    const handleTextSelection = () => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const { selectionStart, selectionEnd } = textarea;

        if (selectionStart !== selectionEnd) {
            setSelection({
                start: selectionStart,
                end: selectionEnd
            });
            setShowToolbar(true);

            // Установка позиции тулбара
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const rect = range.getBoundingClientRect();

                setToolbarPosition({
                    top: rect.top - 40,
                    left: rect.left
                });
            }
        } else {
            setShowToolbar(false);
            setSelection(null);
        }
    };

    // Функция для выделения текста определенным стилем
    const highlightSelectedText = () => {
        if (!selection) return;

        const { start, end } = selection;

        // Проверяем, не перекрывается ли выделение с существующими
        const overlapping = tempHighlights.some(
            highlight =>
                (start >= highlight.start && start < highlight.end) ||
                (end > highlight.start && end <= highlight.end) ||
                (start <= highlight.start && end >= highlight.end)
        );

        if (overlapping) {
            alert('Нельзя создавать перекрывающиеся выделения');
            return;
        }

        const newHighlight = { start, end, style: 'bold' };

        // Добавляем новое выделение и сортируем по начальной позиции
        const newHighlights = [...tempHighlights, newHighlight]
            .sort((a, b) => a.start - b.start);

        setTempHighlights(newHighlights);
        setShowToolbar(false);
        setSelection(null);
    };

    // Функция для удаления выделения
    const removeHighlight = (highlightIndex) => {
        const updatedHighlights = [...tempHighlights];
        updatedHighlights.splice(highlightIndex, 1);
        setTempHighlights(updatedHighlights);
    };

    // Функция для добавления маркера списка в начало строки
    const addListItem = () => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const { selectionStart } = textarea;
        const currentText = textarea.value;

        // Находим начало текущей строки
        let lineStart = selectionStart;
        while (lineStart > 0 && currentText[lineStart - 1] !== '\n') {
            lineStart--;
        }

        // Добавляем маркер в начало строки
        const beforeMarker = currentText.substring(0, lineStart);
        const afterMarker = currentText.substring(lineStart);
        const newText = beforeMarker + '• ' + afterMarker;

        setTempText(newText);

        // Устанавливаем курсор после маркера
        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = lineStart + 2;
        }, 0);
    };

    // Функция для форматирования текста с выделенными частями
    const formatTextWithHighlights = (text, highlights) => {
        if (!text) {
            return '';
        }

        let parts = [];
        let lastIndex = 0;

        // Если есть выделения, сначала обрабатываем их
        if (highlights && highlights.length > 0) {
            // Сортируем выделения по начальной позиции
            const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);

            // Создаем новую строку с выделенными частями
            sortedHighlights.forEach((highlight, index) => {
                // Добавляем текст до выделения
                if (highlight.start > lastIndex) {
                    parts.push(text.substring(lastIndex, highlight.start));
                }

                // Добавляем выделенный текст с встроенными стилями
                const highlightedText = text.substring(highlight.start, highlight.end);
                parts.push(`<span style="font-weight:700;color:white;display:inline !important;" class="highlight-bold">${highlightedText}</span>`);

                lastIndex = highlight.end;
            });

            // Добавляем оставшийся текст после последнего выделения
            if (lastIndex < text.length) {
                parts.push(text.substring(lastIndex));
            }
        } else {
            // Если нет выделений, обрабатываем только маркеры списка
            const lines = text.split('\n');
            return lines.map((line, lineIndex) => {
                if (line.startsWith('• ')) {
                    return (
                        <div key={lineIndex} className="list-item" style={{ display: 'flex', marginBottom: '5px' }}>
                            <span className="list-marker" style={{ marginRight: '5px' }}>•</span>
                            <span className="list-text">{line.substring(2)}</span>
                        </div>
                    );
                }

                // Если строка пустая, возвращаем пустой div с высотой строки
                if (!line.trim()) {
                    return <div key={lineIndex} style={{ height: '1em' }}>&nbsp;</div>;
                }

                return <div key={lineIndex}>{line}</div>;
            });
        }

        // Объединяем все части текста
        const fullText = parts.join('');

        // Разбиваем на строки и обрабатываем списки
        const lines = fullText.split('\n');
        return lines.map((line, lineIndex) => {
            // Обрабатываем маркеры списка
            if (line.startsWith('• ')) {
                const markerText = '• ';
                const restOfLine = line.substring(markerText.length);
                return (
                    <div key={lineIndex} className="list-item" style={{ display: 'flex', marginBottom: '5px' }}>
                        <span className="list-marker" style={{ marginRight: '5px' }}>•</span>
                        <span className="list-text" dangerouslySetInnerHTML={{ __html: restOfLine }} />
                    </div>
                );
            }

            // Если строка пустая, возвращаем пустой div с высотой строки
            if (!line.trim()) {
                return <div key={lineIndex} style={{ height: '1em' }}>&nbsp;</div>;
            }

            // Обычная строка с возможными выделениями
            return (
                <div key={lineIndex} dangerouslySetInnerHTML={{ __html: line }} />
            );
        });
    };

    return (
        <>
            {/* Лоадер */}
            {loading && <div className="faq-page-loader"><LoaderInside /></div>}

            <div className="faq-content-container">
                {/* Заголовок страницы с кнопкой назад */}
                <div className="faq-content-header">
                    <button className="back-button" onClick={handleBack}>
                        <img src={backIcon} alt="Назад" />
                    </button>
                    <h1>{getCategoryTitle()}</h1>
                </div>

                {/* Кнопка создания новой FAQ для админов */}
                {adminMode && !editingFaq && (
                    <div style={{ width: '100%', marginBottom: '20px' }}>
                        <button
                            className="create-faq-button"
                            onClick={handleCreateNew}
                        >
                            Создать новый блок
                        </button>
                    </div>
                )}

                {/* Панель редактирования для админа */}
                {adminMode && editingFaq ? (
                    <div className="admin-edit-panel">
                        <h3 className="admin-edit-header">Редактирование FAQ</h3>
                        <div className="admin-edit-form">
                            <label>Текст FAQ:</label>
                            <div className="text-editor">
                                <div className="editor-wrapper">
                                    {/* Кнопки форматирования текста */}
                                    <div className="editor-toolbar">
                                        <button
                                            type="button"
                                            onClick={addListItem}
                                            className="format-button"
                                        >
                                            Добавить маркер списка
                                        </button>
                                    </div>

                                    {/* Тулбар для выделения текста */}
                                    {showToolbar && (
                                        <div
                                            className="text-selection-toolbar"
                                            style={{
                                                top: toolbarPosition.top + 'px',
                                                left: toolbarPosition.left + 'px'
                                            }}
                                        >
                                            <button onClick={highlightSelectedText}>
                                                Выделить
                                            </button>
                                        </div>
                                    )}

                                    <textarea
                                        ref={textAreaRef}
                                        value={tempText}
                                        onChange={(e) => updateText(e.target.value)}
                                        onSelect={handleTextSelection}
                                        placeholder="Введите текст..."
                                        rows={10}
                                    />

                                    {/* Превью текста с выделениями */}
                                    {tempText && (
                                        <div className="preview-section">
                                            <h4>Предпросмотр:</h4>
                                            <div className="faq-card-content preview-content">
                                                {formatTextWithHighlights(tempText, tempHighlights)}
                                            </div>
                                        </div>
                                    )}

                                    {/* Список выделений */}
                                    {tempHighlights.length > 0 && (
                                        <div style={{ marginTop: '10px' }}>
                                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '5px' }}>
                                                Выделенные части (кликните для удаления):
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                {tempHighlights.map((highlight, highlightIndex) => (
                                                    <button
                                                        key={highlightIndex}
                                                        onClick={() => removeHighlight(highlightIndex)}
                                                        style={{
                                                            background: 'rgba(255,255,255,0.4)',
                                                            border: 'none',
                                                            padding: '3px 8px',
                                                            borderRadius: '0',
                                                            color: 'white',
                                                            fontSize: '11px',
                                                            cursor: 'pointer',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        "{tempText.substring(highlight.start, highlight.end)}"
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="admin-edit-buttons">
                                <button className="save-button" onClick={saveChanges}>
                                    Сохранить
                                </button>
                                <button className="cancel-button" onClick={cancelEditing}>
                                    Отмена
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Контейнер для карточек FAQ */
                    <div className="faq-cards-container">
                        {faqs.length > 0 ? (
                            faqs.map((faq) => (
                                <div key={faq._id} className="faq-card">
                                    <div className="faq-card-content">
                                        {formatTextWithHighlights(faq.text, faq.highlights)}
                                    </div>

                                    {adminMode && (
                                        <div className="faq-card-actions">
                                            <button onClick={() => moveUp(faq)} className="order-button up-button">
                                                ↑
                                            </button>
                                            <button onClick={() => moveDown(faq)} className="order-button down-button">
                                                ↓
                                            </button>
                                            <button
                                                onClick={() => startEditing(faq)}
                                                className="edit-button"
                                            >
                                                Редактировать
                                            </button>
                                            <button
                                                onClick={() => deleteFAQ(faq._id)}
                                                className="delete-button"
                                            >
                                                Удалить
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
                                {loading ? 'Загрузка...' : 'Нет доступных FAQ в данной категории'}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default FAQContent; 